import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material'
import { useGridApiRef } from '@mui/x-data-grid'
import { useIsMutating } from '@tanstack/react-query'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useMemo, useState } from 'react'
import ExportCSV from '../components/ExportCSV'
import PaymentHistoryTab from '../components/PaymentHistoryTab'
import PaymentSummary from '../components/PaymentSummary'
import RequeuePaymentButton from '../components/RequeuePaymentButton'
import TransactionGrid from '../components/TransactionGrid'
import { ACH_EXPEDITED, ACH_NEXT_DAY, ACH_SAME_DAY } from '../constants'
import {
  type Payment,
  type PaymentOption,
  useGetPaymentQuery,
  useHoldPaymentMutation,
  useReleasePaymentsMutation,
  useStartReviewMutation,
  useUnholdPaymentMutation,
  useUpdateStatusFromSubmittedToFailedMutation,
} from '../generated/graphql'
import { getRoutableUrl } from '../utils/util'

function ReleasePaymentButtons({ onClick, disabled }: { onClick: (paymentOption: string) => void; disabled: boolean }) {
  const [paymentOption, setPaymentOption] = useState(ACH_EXPEDITED)

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <Button variant="contained" color="primary" disabled={disabled} onClick={() => onClick(paymentOption)}>
        Release
      </Button>
      <Select
        value={paymentOption}
        onChange={e => setPaymentOption(e.target.value)}
        size="small"
        sx={{ minWidth: 250 }}
      >
        <MenuItem value={ACH_EXPEDITED}>ACH: 2-3 business days</MenuItem>
        <MenuItem value={ACH_NEXT_DAY}>ACH: 1 business day</MenuItem>
        <MenuItem value={ACH_SAME_DAY}>ACH: Same business day</MenuItem>
      </Select>
    </Box>
  )
}

export default function PaymentDetails() {
  const { paymentId: paymentIdParam } = useParams({
    from: '/payments/$paymentId',
  })
  const search = useSearch({ from: '/payments/$paymentId' })
  const navigate = useNavigate({ from: '/payments/$paymentId' })
  const paymentId = parseInt(paymentIdParam, 10)
  const { enqueueSnackbar } = useSnackbar()
  const gridApiRef = useGridApiRef()

  const activeTab = useMemo(() => (search?.tab === 'history' ? 1 : 0), [search?.tab])

  const handleTabChange = (_: React.SyntheticEvent, value: number) => {
    const tab = value === 1 ? 'history' : 'details'
    navigate({ search: { tab }, replace: true })
  }

  const { data, isLoading, refetch } = useGetPaymentQuery(
    { paymentId },
    {
      enabled: !Number.isNaN(paymentId),
    }
  )

  const holdPaymentMutation = useHoldPaymentMutation({
    onSuccess: () => {
      enqueueSnackbar('Payment held successfully', { variant: 'success' })
      refetch()
    },
    onError: (error: Error) => {
      console.error(error)
      enqueueSnackbar(error?.message || 'Failed to hold payment', {
        variant: 'error',
      })
    },
  })

  const unholdPaymentMutation = useUnholdPaymentMutation({
    onSuccess: () => {
      enqueueSnackbar('Payment unheld successfully', { variant: 'success' })
      refetch()
    },
    onError: (error: Error) => {
      console.error(error)
      enqueueSnackbar(error?.message || 'Failed to unhold payment', {
        variant: 'error',
      })
    },
  })

  const startReviewMutation = useStartReviewMutation({
    onSuccess: () => {
      enqueueSnackbar('Review started successfully', { variant: 'success' })
      refetch()
    },
    onError: (error: Error) => {
      console.error(error)
      enqueueSnackbar(error?.message || 'Failed to start review', {
        variant: 'error',
      })
    },
  })

  const releasePaymentsMutation = useReleasePaymentsMutation({
    onSuccess: () => {
      enqueueSnackbar('Payment released successfully', { variant: 'success' })
      refetch()
    },
    onError: (error: Error) => {
      console.error(error)
      enqueueSnackbar(error?.message || 'Failed to release payment', {
        variant: 'error',
      })
    },
  })

  const updateStatusMutation = useUpdateStatusFromSubmittedToFailedMutation({
    onSuccess: () => {
      enqueueSnackbar('Payment status updated successfully', {
        variant: 'success',
      })
      refetch()
    },
    onError: (error: Error) => {
      console.error(error)
      enqueueSnackbar(error?.message || 'Failed to update payment status', {
        variant: 'error',
      })
    },
  })

  const handleHoldPayment = () => {
    holdPaymentMutation.mutate({ paymentId })
  }

  const handleUnholdPayment = () => {
    unholdPaymentMutation.mutate({ paymentId })
  }

  const handleReviewPayment = () => {
    startReviewMutation.mutate({ paymentIds: [paymentId] })
  }

  const handleReleasePayment = (paymentOption: string) => {
    releasePaymentsMutation.mutate({
      paymentIds: [paymentId],
      paymentOption: paymentOption as PaymentOption,
    })
  }

  const handleStuckInSubmittedPayment = () => {
    updateStatusMutation.mutate({ paymentId })
  }

  const requeuePaymentsMutating = useIsMutating({ mutationKey: ['RequeuePayments'] as const }) > 0

  const actionRunning =
    holdPaymentMutation.isPending ||
    unholdPaymentMutation.isPending ||
    startReviewMutation.isPending ||
    releasePaymentsMutation.isPending ||
    updateStatusMutation.isPending ||
    requeuePaymentsMutating

  const paymentData = data?.getPayment as Payment | undefined

  const trueUpItems = useMemo(
    () => paymentData?.items?.filter(item => item?.referenceType === 'TRUE_UP') ?? [],
    [paymentData?.items]
  )

  if (isLoading || !paymentData) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Payment #{paymentId}
        </Typography>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box
        gap={2}
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h5">Payment #{paymentId}</Typography>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Details" />
          <Tab label="History" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 2,
              mb: 2,
              flexWrap: 'wrap',
            }}
          >
            {paymentData.paymentProcessorKind && paymentData.externalRefId && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() =>
                  window.open(
                    `${getRoutableUrl(paymentData.paymentProcessorKind)}/dashboard/payments/payables/all/${paymentData.externalRefId}/item`,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
                startIcon={<OpenInNewIcon />}
              >
                View in Routable
              </Button>
            )}
            {paymentData.status === 'OPEN' && (
              <Button variant="outlined" color="primary" onClick={handleReviewPayment} disabled={actionRunning}>
                Start Review
              </Button>
            )}
            {paymentData.status === 'UNDER_REVIEW' && (
              <>
                <Button variant="outlined" color="secondary" onClick={handleHoldPayment} disabled={actionRunning}>
                  Hold
                </Button>
                <ReleasePaymentButtons onClick={handleReleasePayment} disabled={actionRunning} />
              </>
            )}
            {paymentData.status === 'HELD' && (
              <Button variant="outlined" color="primary" onClick={handleUnholdPayment} disabled={actionRunning}>
                Unhold
              </Button>
            )}
            {(paymentData.status === 'ENQUEUED_FOR_RELEASE' || paymentData.status === 'FAILED') && (
              <RequeuePaymentButton
                onSuccess={() => refetch()}
                selectedPaymentIds={[paymentId]}
                disabled={actionRunning}
                sx={{ mb: 0 }}
              />
            )}
            {paymentData.status === 'SUBMITTED' && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleStuckInSubmittedPayment}
                disabled={actionRunning}
              >
                Update to Failed
              </Button>
            )}
          </Box>

          <PaymentSummary payment={paymentData} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 1,
              mb: 2,
            }}
          >
            <Typography variant="h6">Ledger Items</Typography>
            <ExportCSV
              ref={gridApiRef}
              disabled={!paymentData.items || paymentData.items.length === 0}
              fileName={`payment-${paymentId}-ledger-items`}
            />
          </Box>
          {paymentData.items && <TransactionGrid rows={paymentData.items} loading={isLoading} apiRef={gridApiRef} />}

          {trueUpItems.length > 0 && (
            <Paper sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                True Up Details
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {trueUpItems.length > 1 && <TableCell>Reference ID</TableCell>}
                    <TableCell>Pay Period (Inclusive)</TableCell>
                    <TableCell>Gross Earnings</TableCell>
                    <TableCell>Net Earnings Floor</TableCell>
                    <TableCell>Engaged Miles</TableCell>
                    <TableCell>Engaged Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trueUpItems.map(item => (
                    <TableRow key={item?.id}>
                      {trueUpItems.length > 1 && <TableCell>{item?.referenceId ?? '-'}</TableCell>}
                      <TableCell>
                        {(() => {
                          const toMMDDYY = (iso: string) => {
                            const [y, m, d] = iso.split('T')[0].split('-')
                            return `${m}/${d}/${y.slice(2)}`
                          }
                          const start = item?.payPeriodStartDate ? toMMDDYY(item?.payPeriodStartDate) : null
                          const end = item?.payPeriodEndDate
                            ? (() => {
                                const d = new Date(item?.payPeriodEndDate as string)
                                d.setUTCDate(d.getUTCDate() - 1)
                                return toMMDDYY(d.toISOString())
                              })()
                            : null
                          return start && end ? `${start} – ${end}` : (start ?? end ?? '-')
                        })()}
                      </TableCell>
                      <TableCell>
                        {item?.grossTotalCents != null
                          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                              item?.grossTotalCents / 100
                            )
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {item?.netEarningsFloorCents != null
                          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                              item?.netEarningsFloorCents / 100
                            )
                          : '-'}
                      </TableCell>
                      <TableCell>{item?.engagedMiles != null ? item?.engagedMiles.toFixed(2) : '-'}</TableCell>
                      <TableCell>
                        {item?.engagedTimeMinutes != null
                          ? (() => {
                              const minutes = item?.engagedTimeMinutes as number
                              const h = Math.floor(minutes / 60)
                              const m = Math.round(minutes % 60)
                              return h > 0 ? `${h}h ${m}m` : `${m}m`
                            })()
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      {activeTab === 1 && <PaymentHistoryTab history={paymentData.history} />}
    </Box>
  )
}
